import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, CheckCircle, Shield, Award, MapPin } from 'lucide-react';

const PHONE = "587-355-FLOW (3569)";

export const Hero = () => {
  return (
    <section 
      data-testid="hero-section" 
      className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-gray-50 to-white"
    >
      {/* Background Image with Light Overlay (optimized: WebP + JPEG fallback, responsive) */}
      <picture>
        <source
          type="image/webp"
          srcSet="/images/flowforge/basement-roughin-1-768.webp 768w, /images/flowforge/basement-roughin-1-1280.webp 1280w"
          sizes="100vw"
        />
        <img
          src="/images/flowforge/basement-roughin-1-1280.jpg"
          srcSet="/images/flowforge/basement-roughin-1-768.jpg 768w, /images/flowforge/basement-roughin-1-1280.jpg 1280w"
          sizes="100vw"
          width="1280"
          height="2276"
          fetchPriority="high"
          decoding="async"
          alt="Flow Forge Mechanical plumbing rough-in work in a Calgary home"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/80" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Trust Badges Row */}
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { icon: Shield, text: 'Licensed & Insured' },
                { icon: MapPin, text: 'Calgary Local' }
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-gray-100">
                  <badge.icon className="w-4 h-4 text-[#1E3A5F]" />
                  <span className="text-xs font-medium text-gray-700">{badge.text}</span>
                </div>
              ))}
            </div>

            {/* Main Headline */}
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-gray-900 mb-6 leading-tight">
              Calgary Plumbing, Heating &amp; Gas Fitting
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-gray-600 mb-4 max-w-lg">
              Professional Plumbing, Heating, Air Conditioning & Gas Fitting services. 
              Quality workmanship from our licensed, insured team.
            </p>
            
            {/* Service Scope */}
            <p className="text-[#1E3A5F] font-semibold mb-6">
              Residential • Building Maintenance • Light Commercial
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                'Licensed & Insured',
                'Calgary Local',
                'Upfront Pricing',
                'Clear, Written Quotes'
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link
                to="/contact"
                data-testid="hero-book-btn"
                className="btn-primary rounded-lg flex items-center justify-center gap-2"
              >
                Request Service
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:+15873553569`}
                data-testid="hero-call-btn"
                className="btn-secondary rounded-lg flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>

            {/* Phone Number */}
            <a
              href={`tel:+15873553569`}
              data-testid="hero-phone"
              className="inline-flex items-center gap-3 text-gray-800 hover:text-[#1E3A5F] transition-colors"
            >
              <div className="p-3 bg-[#DC2626] rounded-full">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <span className="block text-xs text-gray-500">Call or Text</span>
                <span className="text-xl font-heading font-bold">{PHONE}</span>
              </div>
            </a>
          </motion.div>

          {/* Right Content - Service Quick Links */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <h3 className="font-heading font-bold text-xl text-gray-900 mb-6">
                How Can We Help Today?
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Plumbing Services', path: '/plumbing', desc: 'Repairs, installations & more' },
                  { label: 'Heating & Furnace', path: '/heating', desc: 'Keep your home warm' },
                  { label: 'Air Conditioning', path: '/air-conditioning', desc: 'New AC installations' },
                  { label: 'Gas Fitting', path: '/gas-fitting', desc: 'Safe gas connections' },
                  { label: 'Backflow Testing', path: '/plumbing/backflow-testing-calgary', desc: 'City of Calgary certified' }
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-[#1E3A5F]/10 rounded-xl transition-all group border border-transparent hover:border-[#1E3A5F]/20"
                  >
                    <div>
                      <span className="font-semibold text-gray-800 group-hover:text-[#1E3A5F] block">{item.label}</span>
                      <span className="text-xs text-gray-500">{item.desc}</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#1E3A5F] group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
